import type React from 'react'
import { useCallback, useMemo } from 'react'
import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import Sidebar, { type SpecificSidebarProps } from '../Sidebar'
import SoundDesignBoxListFilters from './SoundDesignBoxListFilters'
import SoundDesignBox from './SoundDesignBox'
import CardPlaceholder from '../../common/CardPlaceholder'
import { fetchSoundDesigns } from '@/features/sound-design/domain/use-cases/fetch-sound-designs.ts'
import type { SoundDesignFilters } from '@/features/sound-design/domain/models/sound-design-filters.ts'
import { useInfiniteQuery, useQuery } from '@tanstack/react-query'

interface SoundDesignFiltersStore {
  filters: SoundDesignFilters
  searchTerm: string
}

export const useSoundDesignFiltersStore = create(
  persist<SoundDesignFiltersStore>(() => ({ filters: {}, searchTerm: '' }), {
    name: 'sound-design-filters',
  })
)

const SoundDesignBoxList: React.FC<SpecificSidebarProps> = (props) => {
  const { filters, searchTerm } = useSoundDesignFiltersStore()

  const { data, fetchNextPage, isFetchingNextPage, isLoading } =
    useInfiniteQuery({
      queryKey: ['sound-design-list', filters, searchTerm],
      queryFn: async ({ pageParam = 0 }) =>
        await fetchSoundDesigns(pageParam, filters, searchTerm),
      getNextPageParam: (lastPage, pages) =>
        lastPage.page < lastPage.totalPages ? pages.length : undefined,
      initialPageParam: 0,
    })

  const { data: parametersData } = useQuery({
    queryKey: ['sound-design-parameters', searchTerm],
    queryFn: () => fetchSoundDesigns(0, {}, searchTerm),
    staleTime: Infinity,
  })

  const soundData = useMemo(
    () => data?.pages.flatMap((page) => page.items) ?? [],
    [data]
  )
  const parameters = parametersData?.parameters
  const noResults = !isLoading && !isFetchingNextPage && soundData.length === 0

  const handleSearch = useCallback((term: string) => {
    useSoundDesignFiltersStore.setState({ searchTerm: term })
  }, [])

  const handleSetFilters = useCallback(
    (
      f:
        | SoundDesignFilters
        | ((prevFilters: SoundDesignFilters) => SoundDesignFilters)
    ) => {
      const next =
        typeof f === 'function'
          ? f(useSoundDesignFiltersStore.getState().filters)
          : f
      useSoundDesignFiltersStore.setState({ filters: next })
    },
    []
  )

  return (
    <Sidebar
      {...props}
      title={'ls_sidebar_sound_design_title'}
      searchKey={'ls_sidebar_sound_design_search'}
      searchAction={handleSearch}
      fetchMoreData={fetchNextPage}
      noResults={noResults}
    >
      <div className="flex flex-col gap-4 bg-zinc-900 p-4 pr-6">
        <SoundDesignBoxListFilters
          setFilters={handleSetFilters}
          parameters={parameters}
          filters={filters}
        />
        <div className="flex flex-col gap-2">
          {soundData.map((sound) => (
            <SoundDesignBox key={sound.id} sound={sound} />
          ))}
          {(isLoading || isFetchingNextPage) &&
            Array.from({ length: 5 }, (_, index) => (
              <CardPlaceholder key={index} height={168} />
            ))}
        </div>
      </div>
    </Sidebar>
  )
}

export default SoundDesignBoxList
